import { ArrowRightIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { useContext, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { cn } from '@/lib/utils'

import { StackedBarChartContext } from './stacked-bar-chart-context'
import type { StackedBarChartSegmentData } from './types'

/**
 * X-offset of the tooltip from the bar.
 */
const X_OFFSET = -5

interface Props {
  data: StackedBarChartSegmentData[]
  formatter?: (value: number) => ReactNode
  labelFormatter?: (datum: StackedBarChartSegmentData) => ReactNode
  container?: React.RefObject<HTMLDivElement>
  onClick?: (datum: StackedBarChartSegmentData) => void
  render?: (datum: StackedBarChartSegmentData, locked?: boolean) => ReactNode
}

export const StackedBarChartTooltip: React.FC<Props> = ({
  data,
  formatter,
  labelFormatter,
  container,
  render,
  onClick,
}) => {
  const ctx = useContext(StackedBarChartContext)
  const ref = useRef<HTMLDivElement>(null)
  const [size, setSize] = useState({ w: 0, h: 0 })

  useLayoutEffect(() => {
    if (ref.current) {
      setSize({
        w: ref.current.offsetWidth,
        h: ref.current.offsetHeight,
      })
    }
  }, [ctx?.hovered])

  const hovered = ctx?.hovered ?? ctx?.tooltipHovered ?? null

  if (!ctx || !hovered || !container?.current) {
    return null
  }

  const { index, xBegin, xEnd, locked } = hovered

  const colors_ = ctx.colors
  const color = colors_[index % colors_.length]
  const datum = data[index]
  const label = labelFormatter?.(datum) ?? datum.name
  const value = formatter?.(datum.value) ?? datum.value
  const segmentWidth = xEnd - xBegin
  const offset = (segmentWidth / 2 < Math.abs(X_OFFSET) ? 2 : 1) * X_OFFSET

  const top = ctx.height / 2 - size.h / 2
  let left = xEnd + offset

  const shouldFlip = left + size.w > ctx.width

  // Flip horizontally if overflows right.
  if (shouldFlip) {
    left = xBegin + offset - size.w
  }

  return createPortal(
    <div
      className={cn('absolute', 'flex items-center')}
      style={{ left, top, transition: 'all 280ms ease-out' }}
      onMouseEnter={() => ctx.setTooltipHovered(hovered)}
      onMouseLeave={() => ctx.setTooltipHovered(null)}
    >
      {!shouldFlip && (
        <div
          style={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '8px 13px 8px 0',
            borderColor: 'transparent rgb(15 23 42) transparent transparent',
            transform: 'rotate(0deg)',
          }}
        />
      )}

      <div ref={ref} className={cn('rounded-[10px] px-3 py-2', 'bg-slate-900')}>
        {render ? (
          render(datum, locked)
        ) : (
          <div className={cn('flex gap-1')}>
            <div className={cn('mt-[6px] h-[12px] w-[12px] rounded-[6px]')} style={{ backgroundColor: color }} />
            <div className={cn('flex flex-col', 'whitespace-nowrap text-[14px] text-white')}>
              <div
                className={cn('flex flex-1 items-center', 'rounded-md px-[7px] py-[2px]', {
                  'hover:bg-slate-800': !!onClick,
                  'cursor-pointer': !!onClick,
                })}
                onClick={() => onClick?.(datum)}
              >
                <div className={cn('pr-4', 'font-[700]')}>{label}</div>
                <ArrowRightIcon className={cn('h-4 w-4 text-white', 'transition-opacity')} />
              </div>
              <div className={cn('pl-[7px]', 'font-[300]')}>
                {(datum.ratio * 100).toFixed(2).replace(/\.?0+$/, '')}% ({value})
              </div>
            </div>
          </div>
        )}
      </div>

      {shouldFlip && (
        <div
          style={{
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: '8px 0 8px 13px',
            borderColor: 'transparent transparent transparent rgb(15 23 42)',
            transform: 'rotate(0deg)',
          }}
        />
      )}
    </div>,
    container.current
  )
}
StackedBarChartTooltip.displayName = 'StackedBarChartTooltip'
